import React, { useCallback, useMemo } from 'react';
import SimpleBar from 'simplebar-react';

import { Toggle } from 'components/elements/Toggle/Toggle';
import CloseIcon from 'components/newListeningExperience/icons/CloseIcon';
import { useTranslation } from 'hooks/useTypedTranslation';
import { AnalyticsEventKey, logAnalyticsEvent } from 'modules/analytics/logAnalyticsEvent';
import { LibraryButtonGroup } from 'modules/library/components/LibraryButtonGroup/LibraryButtonGroup';
import { THEME_SELECTOR_BUTTONS, ThemeMode } from 'modules/listening/features/settings/settings';
import { listeningSettingsStoreActions, useListeningSettingsStore } from 'modules/listening/features/settings/settingsStore';
import { DropdownItem } from 'modules/user-page/components/common/Dropdown';
import { ColorId } from 'utils/highlight-colors';

import { AppThemeIcon, ChevronLeftIcon, CursorColorIcon, FontIcon, RadioCheckIcon } from '../icons';
import { ColorDropDown } from './ColorDropDown';
import { DefaultClassicFontFamily, useFontOptions } from './hooks/useFontOptions';

export type AppearancePanelProps = {
  isEPUB: boolean;
  isPDF: boolean;
  onBack: () => void;
  onClose: () => void;
};

const AppearancePanel: React.FC<AppearancePanelProps> = ({ isEPUB, isPDF, onBack, onClose }) => {
  const { t } = useTranslation('common');
  const fontOptions = useFontOptions(isEPUB);

  const cursorColor = useListeningSettingsStore(state => state.cursorColor);
  const highlightSentence = useListeningSettingsStore(state => state.highlightSentence);
  const themeMode = useListeningSettingsStore(state => state.themeMode);
  const darkMode = useListeningSettingsStore(state => state.darkMode);
  const epubFontFamily = useListeningSettingsStore(state => state.epubFontFamily);
  const fontFamily = useListeningSettingsStore(state => state.fontFamily);

  const handleThemeChange = useCallback((theme: ThemeMode) => {
    listeningSettingsStoreActions.setAppTheme(theme);
  }, []);

  const handleCursorColorChange = useCallback((option: DropdownItem) => {
    listeningSettingsStoreActions.setCursorColor(option.id as ColorId);
  }, []);

  const handleHighlightSentenceToggle = useCallback(() => {
    const value = !highlightSentence;
    listeningSettingsStoreActions.setHighlightSentence(value);
  }, [highlightSentence]);

  const handleFontChange = useCallback(
    (fontFamily: string) => {
      if (isEPUB) {
        listeningSettingsStoreActions.setEPUBFontFamily(fontFamily ?? 'default');
      } else {
        listeningSettingsStoreActions.setFontFamily(fontFamily ?? DefaultClassicFontFamily);
      }

      logAnalyticsEvent(AnalyticsEventKey.listeningSettingsFontFamilyChanged, {
        fontFamily: fontFamily ?? 'default'
      });
    },
    [isEPUB]
  );

  const Separator = useMemo(() => {
    const lightColor = '#e9eaf0';
    const darkColor = '#1e1e1f';

    // setting background color using style to prevent flickering on theme change
    const color = darkMode ? darkColor : lightColor;

    return (
      <div className="py-spl-4">
        <div className="flex">
          <div className="w-5 shrink-0" />
          <div className="w-spl-9 shrink-0" />
          <div className="h-[0.5px] flex-1 transition-colors duration-200 ease-in-out" style={{ backgroundColor: color }} />
        </div>
      </div>
    );
  }, [darkMode]);

  return (
    <>
      <div className="flex bg-bg-prim-w-110 justify-between relative items-center px-spl-7 py-spl-6">
        <button className="hover:opacity-75 active:opacity-50" onClick={onBack}>
          <ChevronLeftIcon className="size-5 text-icn-txt-prim" />
        </button>
        <div className="text-subheading-4 grow text-center text-icn-txt-prim">{t('Appearance')}</div>
        <button className="hover:opacity-75 active:opacity-50">
          <CloseIcon onClick={onClose} className="text-icn-txt-prim" />
        </button>
      </div>

      <SimpleBar className="flex-shrink min-h-0">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-spl-7 py-spl-6">
              <div className="flex items-center gap-spl-6">
                <div className="size-5">
                  <AppThemeIcon className="text-icn-txt-prim" />
                </div>
                <div className="flex flex-col">
                  <span className="text-subheading-5 text-icn-txt-prim">{t('App Theme')}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <LibraryButtonGroup buttons={THEME_SELECTOR_BUTTONS} selected={themeMode} onClick={handleThemeChange} />
              </div>
            </div>

            {Separator}

            <div className="flex items-center justify-between px-spl-7 py-spl-6">
              <div className="flex items-center gap-spl-6">
                <div className="size-5 relative">
                  <CursorColorIcon />
                </div>
                <div className="flex flex-col">
                  <span className="text-subheading-5 text-icn-txt-prim">{t('Cursor Color')}</span>
                </div>
              </div>
              <ColorDropDown isPDF={isPDF} selectedColorId={cursorColor} onSelectionChange={handleCursorColorChange} />
            </div>

            <div className="flex items-center justify-between px-spl-7 py-spl-6 border-hglt-pink-prim">
              <div className="flex items-center gap-spl-6">
                <div className="size-5"></div>
                <div className="flex flex-col">
                  <span className="text-subheading-5 text-icn-txt-prim">{t('Highlight Sentence')}</span>
                </div>
              </div>
              <Toggle state={highlightSentence ? 'checked' : 'default'} onChange={handleHighlightSentenceToggle} />
            </div>
          </div>

          {!isPDF && fontOptions.length > 0 && (
            <>
              {Separator}

              <div className="flex items-center justify-between px-spl-7 py-spl-6">
                <div className="flex items-center gap-spl-6">
                  <div className="size-5 relative">
                    <FontIcon />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-subheading-5 text-icn-txt-prim">{t('Font')}</span>
                  </div>
                </div>
              </div>

              {fontOptions.map(option => {
                const isSelected = isEPUB ? epubFontFamily === option.fontFamily : fontFamily === option.fontFamily;

                return (
                  <button key={option.id} className="flex items-center justify-between px-spl-7 py-spl-5" onClick={() => handleFontChange(option.fontFamily)}>
                    <div className="flex items-center gap-spl-6">
                      <div className="size-5"></div>
                      <div className="flex flex-col">
                        <span
                          className="text-subheading-5 font-normal text-icn-txt-prim"
                          style={{ fontFamily: option.fontFamily !== 'default' ? option.fontFamily : undefined }}
                        >
                          {option.id === 'default' ? t('Default') : option.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center size-4">
                      <div
                        className={`size-4 rounded-full border-[1.5px] transition-colors ${
                          isSelected ? 'border-sf-act-blue bg-sf-act-electric-blue' : 'border-brdr-tert-40-50 bg-transparent hover:border-brdr-blue'
                        }`}
                      >
                        {isSelected && <RadioCheckIcon className="-mt-[1px] -ml-[1px]" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {isPDF && <div className="h-32 w-full bg-transparent" />}
        </div>
      </SimpleBar>
    </>
  );
};

export default AppearancePanel;
